import { pipe } from "fp-ts/lib/function"
import { POApi } from "../POApi"
import * as t from 'io-ts'
import { ZIO } from "@mxt/zio"
import { Maybe } from "@mxt/zio/codec"

export namespace CSService {
  export const NewGADDocumentRequest = t.type({
    id: Maybe(t.string),
    code: t.string,
    documentType: t.string,
    fileName: t.string,
    gaCode: t.string,
    csArea: t.string,
    docId: t.string,
    createdDate: t.string,
    createdBy: t.string,
    modifiedDate: t.string,
    modifiedBy: t.string,
    activityDate: t.string,
    status: t.string,
    transactionType: t.string,
    docNo: t.string,
    document: t.type({
      name: t.string,
      url: t.string,
    }),
    keyID: Maybe(t.string),
    docTypeCode: Maybe(t.string),
    effectiveDate: Maybe(t.string),
    docType: Maybe(t.string),
    docTypeDetail: Maybe(t.string)
  })

  export const NewGADDocumentResponse = t.type({
    body: t.array(t.type({
      keyID: t.string,
      message: t.string
    })),
    status: t.type({
      message: Maybe(t.string)
    })
  })

  export const GADDocumentListRequest = t.type({
    code: t.string,
    documentType: t.string,
    fileName: t.string,
    gaCode: t.string,
    csArea: t.string,
    docId: t.string,
    createdDate: t.string,
    createdBy: t.string,
    modifiedDate: t.string,
    modifiedBy: t.string,
    status: t.string,
    transactionType: t.string,
    docNo: t.string,
    start: t.number,
    size: t.number,
    fromDate: t.string,
    toDate: t.string,
    docTypeCode: t.string,
  })

  export const GADDocumentInfo = t.type({
    id: t.string,
    fileName: t.string,
    gaCode: t.string,
    csArea: t.string,
    docId: t.string,
    createdDate: t.string,
    createdBy: t.string,
    modifiedDate: t.string,
    modifiedBy: t.string,
    activityDate: Maybe(t.string),
    status: t.string,
    urlFilenet: t.string,
    effectiveDate: Maybe(t.string),
    docTypeCode: Maybe(t.string),
    docType: Maybe(t.string),
    docTypeDetail: Maybe(t.string),
  })

  export const GADDocumentListResponse = t.type({
    body: t.array(GADDocumentInfo),
    start: t.number,
    size: t.number,
    total: t.number
  })
  export const DeleteADDocumentListRequest = t.type({
    body: t.array(t.type({
      id: t.string,
      modifiedDate: t.string,
      modifiedBy: t.string,
      status: t.string
    }))
  })

  export const UpdateADDocumentListRequest = t.type({
    body: t.array(t.type({
      id: t.string,
      modifiedDate: t.string,
      modifiedBy: t.string,
      activityDate: t.string,
      status: t.string
    }))
  })

  export const UpdateGADDocumentResponse = t.type({
    status: t.type({
      message: Maybe(t.string)
    })
  })

  export const DocTypeInfo = t.type({
    code: t.string,
    name: t.string
  })

  export const DocTypeDetailInfo = t.type({
    docTypeCode: t.string,
    docNo: t.string,
    docId: t.string,
    name: t.string,
  })

  export const MetaDataInfo =
    t.type({
      transactionType: t.string,
      departmentShortName: t.string,
      department: t.string,
      trigger: t.string,
      doctypeBPM: t.string,
      doctypeVn: t.string,
      doctypeEn: t.string,
      docTypeCode: t.string,
      classFilenet: t.string,
      docID: t.string,
      mainDoc: t.string,
      subDoc: t.string,
      batchNo: t.string,
      type: t.string,
      refNum: t.string
    })


  export type NewGADDocumentRequest = t.TypeOf<typeof NewGADDocumentRequest>
  export type GADDocumentListRequest = t.TypeOf<typeof GADDocumentListRequest>
  export type GADDocumentInfo = t.TypeOf<typeof GADDocumentInfo>
  export type DeleteADDocumentListRequest = t.TypeOf<typeof DeleteADDocumentListRequest>
  export type UpdateADDocumentListRequest = t.TypeOf<typeof UpdateADDocumentListRequest>
  export type DocTypeInfo = t.TypeOf<typeof DocTypeInfo>
  export type DocTypeDetailInfo = t.TypeOf<typeof DocTypeDetailInfo>
  export type MetaDataInfo = t.TypeOf<typeof MetaDataInfo>

  export const addNewGADDocument = (bodyData: NewGADDocumentRequest[]) => pipe(
    POApi.post(`prunet/add-cs-document`)(NewGADDocumentResponse)(bodyData),
    ZIO.map((newResponse) => newResponse)
  )

  export const getGADDocumentList = (bodyData: GADDocumentListRequest) => pipe(
    POApi.post(`prunet/search-cs-document`)(GADDocumentListResponse)(bodyData),
    ZIO.map((response) => response)
  )

  export const deleteGADDocumentList = (data: DeleteADDocumentListRequest) => pipe(
    POApi.post(`prunet/update-cs-document`)(UpdateGADDocumentResponse)(data),
    ZIO.map((response) => response)
  )

  export const updateGADDocumentList = (data: UpdateADDocumentListRequest) => pipe(
    POApi.post(`prunet/update-cs-document`)(UpdateGADDocumentResponse)(data),
    ZIO.map((response) => response)
  )

  export const addNewProcessGuidelineDoc = (bodyData: NewGADDocumentRequest[]) => pipe(
    POApi.post(`prunet/add-cs-document`)(NewGADDocumentResponse)(bodyData),
    ZIO.map((newResponse) => newResponse)
  )

  export const getProcessGuidelineDocList = (bodyData: GADDocumentListRequest) => pipe(
    POApi.post(`prunet/search-cs-document`)(GADDocumentListResponse)(bodyData),
    ZIO.map((response) => response)
  )

  export const getProcessDocTypeDetailList = () => pipe(
    POApi.get(`prunet/doctype`)(t.type({
      body: t.type({
        docTypeList: t.array(DocTypeInfo),
        docTypeDetailList: t.array(DocTypeDetailInfo)
      })
    }))
  )

  export const getMetaDataByDocID = (docIdList: Array<string>) => {
    return pipe(
      POApi.post('pulseops/api/v1/filenet/get-all-metadata')(t.type({
        data: t.array(MetaDataInfo)
      }))({
        docId: docIdList
      }),
      ZIO.map((response) => {
        const metaDataItem =
          response && response.data.length > 0
            ? response.data[0]
            : null
        return metaDataItem
      })
    )
  }
}